import React from 'react';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import RateReviewIcon from '@mui/icons-material/RateReview';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ScheduleIcon from '@mui/icons-material/Schedule';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import SpeedIcon from '@mui/icons-material/Speed';
import InfoIcon from '@mui/icons-material/Info';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import BuildIcon from '@mui/icons-material/Build';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import ForumIcon from '@mui/icons-material/Forum';
import {
  DISKUSSION_VIEW,
  SCHWACHSTELLEN_REVIEW,
  VERWALTUNG_MANAGE,
  URLAUB_APPROVE,
  WORKBENCH_USE,
} from '../constants/permissions';
import type { WidgetDefinition } from './types';

export const WIDGET_REGISTRY: WidgetDefinition[] = [
  {
    id: 'notifications',
    name: 'Benachrichtigungen',
    description: 'Ungelesene In-App Benachrichtigungen',
    icon: NotificationsActiveIcon,
    defaultW: 6,
    defaultH: 4,
    constraints: { minW: 3, maxW: 12, minH: 2, maxH: 8 },
    component: React.lazy(() => import('./components/NotificationsWidget')),
    category: 'personal',
  },
  {
    id: 'review-queue',
    name: 'Review-Queue',
    description: 'Anzahl offener Schwachstellen-Reviews',
    icon: RateReviewIcon,
    defaultW: 3,
    defaultH: 2,
    constraints: { minW: 2, maxW: 8, minH: 2, maxH: 6 },
    component: React.lazy(() => import('./components/ReviewQueueWidget')),
    requiredPermission: SCHWACHSTELLEN_REVIEW,
    category: 'audit',
  },
  {
    id: 'today-schedule',
    name: 'Heute',
    description: 'Heutige Preflights und laufende Audits',
    icon: CalendarTodayIcon,
    defaultW: 12,
    defaultH: 4,
    constraints: { minW: 3, maxW: 12, minH: 2, maxH: 10 },
    component: React.lazy(() => import('./components/TodayScheduleWidget')),
    category: 'audit',
  },
  {
    id: 'upcoming-events',
    name: 'Anstehende Termine',
    description: 'Tabelle der nächsten Termine (Durchführungen & Preflights)',
    icon: ScheduleIcon,
    defaultW: 12,
    defaultH: 4,
    constraints: { minW: 3, maxW: 12, minH: 2, maxH: 10 },
    component: React.lazy(() => import('./components/UpcomingEventsWidget')),
    category: 'audit',
  },
  {
    id: 'recent-changes',
    name: 'Letzte Änderungen',
    description: 'Zuletzt erstellte Audits, Schwachstellen, Projekte etc.',
    icon: HistoryIcon,
    defaultW: 4,
    defaultH: 4,
    constraints: { minW: 2, maxW: 10, minH: 2, maxH: 8 },
    component: React.lazy(() => import('./components/RecentChangesWidget')),
    category: 'info',
  },
  {
    id: 'last-used',
    name: 'Zuletzt benutzt',
    description: 'Links die letzten Audits, rechts die letzten Schwachstellen',
    icon: RestoreIcon,
    defaultW: 8,
    defaultH: 4,
    constraints: { minW: 6, maxW: 12, minH: 3, maxH: 12 },
    component: React.lazy(() => import('./components/LastUsedWidget')),
    category: 'personal',
  },
  {
    id: 'latest-diskussion-posts',
    name: 'Aktuelle Diskussionen',
    description: 'Neueste sichtbare Diskussionsbeiträge',
    icon: ForumIcon,
    defaultW: 5,
    defaultH: 4,
    constraints: { minW: 3, maxW: 10, minH: 2, maxH: 8 },
    component: React.lazy(() => import('./components/LatestDiskussionPostsWidget')),
    requiredPermission: DISKUSSION_VIEW,
    category: 'info',
  },
  {
    id: 'system-metrics',
    name: 'System-Metriken',
    description: 'CPU, Speicher, DB-Größe und Backup-Status',
    icon: SpeedIcon,
    defaultW: 4,
    defaultH: 4,
    constraints: { minW: 2, maxW: 10, minH: 2, maxH: 8 },
    component: React.lazy(() => import('./components/SystemMetricsWidget')),
    requiredPermission: VERWALTUNG_MANAGE,
    category: 'admin',
  },
  {
    id: 'version-info',
    name: 'Version & Build',
    description: 'Aktuelle Version, Commit und Build-Informationen',
    icon: InfoIcon,
    defaultW: 3,
    defaultH: 3,
    constraints: { minW: 2, maxW: 8, minH: 2, maxH: 8 },
    component: React.lazy(() => import('./components/VersionInfoWidget')),
    category: 'info',
  },
  {
    id: 'my-vacation',
    name: 'Mein Urlaubsstand',
    description: 'Verbleibende, genehmigte und beantragte Urlaubstage',
    icon: BeachAccessIcon,
    defaultW: 3,
    defaultH: 3,
    constraints: { minW: 2, maxW: 6, minH: 2, maxH: 4 },
    component: React.lazy(() => import('./components/MyVacationWidget')),
    category: 'personal',
  },
  {
    id: 'my-hours',
    name: 'Meine Stunden',
    description: 'Gebuchte Stunden des aktuellen Monats pro Projekt',
    icon: AccessTimeIcon,
    defaultW: 4,
    defaultH: 3,
    constraints: { minW: 2, maxW: 8, minH: 2, maxH: 6 },
    component: React.lazy(() => import('./components/MyHoursWidget')),
    category: 'personal',
  },
  {
    id: 'hall-of-fame',
    name: 'Hall of Fame',
    description: 'Top-Pentester des aktuellen Monats mit Punkten',
    icon: EmojiEventsIcon,
    defaultW: 4,
    defaultH: 4,
    constraints: { minW: 2, maxW: 8, minH: 2, maxH: 6 },
    component: React.lazy(() => import('./components/HallOfFameWidget')),
    category: 'audit',
  },
  {
    id: 'workbench-favorites',
    name: 'Workbench-Favoriten',
    description: 'Schnellzugriff auf favorisierte Security-Tools',
    icon: BuildIcon,
    defaultW: 3,
    defaultH: 3,
    constraints: { minW: 2, maxW: 6, minH: 2, maxH: 6 },
    component: React.lazy(() => import('./components/WorkbenchFavoritesWidget')),
    requiredPermission: WORKBENCH_USE,
    category: 'personal',
  },
  {
    id: 'vacation-approvals',
    name: 'Offene Urlaubsanträge',
    description: 'Anzahl offener Urlaubsanträge zur Genehmigung',
    icon: EventBusyIcon,
    defaultW: 2,
    defaultH: 2,
    constraints: { minW: 2, maxW: 6, minH: 2, maxH: 4 },
    component: React.lazy(() => import('./components/VacationApprovalsWidget')),
    requiredPermission: URLAUB_APPROVE,
    category: 'admin',
  },
  {
    id: 'external-systems',
    name: 'Externe Systeme',
    description: 'Metriken externer Umgebungen (Disk, Container, Backups)',
    icon: CloudQueueIcon,
    defaultW: 6,
    defaultH: 5,
    constraints: { minW: 4, maxW: 12, minH: 3, maxH: 10 },
    component: React.lazy(() => import('./components/ExternalSystemsWidget')),
    requiredPermission: VERWALTUNG_MANAGE,
    category: 'admin',
  },
];

export const getWidgetById = (id: string): WidgetDefinition | undefined =>
  WIDGET_REGISTRY.find((w) => w.id === id);
